"use client";

import {
  ScrollArea as RadixScrollArea,
  ScrollAreaCorner as RadixScrollAreaCorner,
  ScrollAreaScrollbar as RadixScrollAreaScrollbar,
  ScrollAreaThumb as RadixScrollAreaThumb,
  ScrollAreaViewport as RadixScrollAreaViewport,
  type ScrollAreaProps as RadixScrollAreaProps,
  type ScrollAreaScrollbarProps as RadixScrollAreaScrollbarProps,
} from "@radix-ui/react-scroll-area";
import { forwardRef } from "react";

import {
  scrollAreaRootVariants,
  scrollAreaViewportVariants,
  scrollBarThumbVariants,
  scrollbarVariants,
} from "./scroll-area.styles";

// -----------------------------------------------------------------------------

export type ScrollBarRef = React.ComponentRef<typeof RadixScrollAreaScrollbar>;

export interface ScrollBarProps extends RadixScrollAreaScrollbarProps {}

const ScrollBar = forwardRef<ScrollBarRef, ScrollBarProps>(
  ({ className, orientation = "vertical", ...props }, ref) => {
    return (
      <RadixScrollAreaScrollbar
        {...props}
        data-slot="scroll-area-scrollbar"
        orientation={orientation}
        className={scrollbarVariants({ className, orientation })}
        ref={ref}
      >
        <RadixScrollAreaThumb
          data-slot="scroll-area-thumb"
          className={scrollBarThumbVariants()}
        />
      </RadixScrollAreaScrollbar>
    );
  },
);

ScrollBar.displayName = "ScrollBar";

export { ScrollBar };

// -----------------------------------------------------------------------------

export type ScrollAreaRef = React.ComponentRef<typeof RadixScrollArea>;

export interface ScrollAreaProps extends RadixScrollAreaProps {
  children: React.ReactNode;
}

const ScrollArea = forwardRef<ScrollAreaRef, ScrollAreaProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <RadixScrollArea
        {...props}
        data-slot="scroll-area"
        className={scrollAreaRootVariants({ className })}
        ref={ref}
      >
        <RadixScrollAreaViewport
          data-slot="scroll-area-viewport"
          className={scrollAreaViewportVariants()}
        >
          {children}
        </RadixScrollAreaViewport>
        <ScrollBar />
        <RadixScrollAreaCorner />
      </RadixScrollArea>
    );
  },
);

ScrollArea.displayName = "ScrollArea";

export { ScrollArea };
